import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MailCheck, Lock } from "lucide-react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { BUSINESS } from "../../lib/constants";

export default function AdminForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-obsidian flex items-center justify-center px-4" data-testid="admin-forgot-password-page">
      <Helmet><title>Reset Password | Orion Services LLC</title></Helmet>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm border border-amber-500/15 rounded-md p-8 bg-carbon">
        <div className="flex items-center gap-3 mb-8">
          <img src={BUSINESS.logo} alt={BUSINESS.name} className="h-10 w-10 rounded-full object-cover" />
          <div>
            <p className="font-display text-lg text-white leading-none">ORION ADMIN</p>
            <p className="text-xs text-zinc-500 flex items-center gap-1 mt-1"><Lock className="h-3 w-3" /> Password Recovery</p>
          </div>
        </div>

        {sent ? (
          <div className="text-center py-4" data-testid="admin-forgot-password-sent">
            <MailCheck className="h-10 w-10 text-amber-light mx-auto mb-4" />
            <p className="text-sm text-zinc-300">If an account exists for <span className="text-white">{email}</span>, a reset link has been emailed. Check your inbox — the link expires in 1 hour.</p>
            <Link to="/admin/login" className="text-xs text-amber-light hover:underline mt-6 inline-block font-mono uppercase">Back to Login</Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            <p className="text-sm text-zinc-400">Enter your admin email and we'll send you a link to reset your password.</p>
            <div>
              <Label className="text-zinc-400 text-xs uppercase font-mono mb-2 block">Email</Label>
              <Input required type="email" data-testid="admin-forgot-password-email" value={email} onChange={(e) => setEmail(e.target.value)} className="!bg-obsidian border-amber-500/15" />
            </div>
            {error && <p className="text-rose-400 text-sm" data-testid="admin-forgot-password-error">{error}</p>}
            <Button type="submit" disabled={loading} data-testid="admin-forgot-password-submit" className="w-full bg-amber-light text-obsidian hover:bg-white font-semibold uppercase tracking-wide">
              {loading ? "Sending..." : "Send Reset Link"}
            </Button>
            <Link to="/admin/login" className="block text-center text-xs text-zinc-500 hover:text-amber-light font-mono uppercase tracking-wide">
              Back to Login
            </Link>
          </form>
        )}
      </motion.div>
    </div>
  );
}
